import { useState, useEffect, useCallback } from "react";
import { 
  Settings, Users, DollarSign, Plus, Trash2, Edit2, 
  Eye, EyeOff, Check, X, Shield, AlertCircle, Package,
  FileText, Download, Upload, Loader2, Percent
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

export default function ConfigTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState("admin");
  
  // Agents state (antes Subadmins)
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentForm, setAgentForm] = useState({
    email: "",
    password: "",
    password_confirm: "",
    name: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    new_password: "",
    new_password_confirm: "",
  });
  
  // Service Catalog state
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [catalogForm, setCatalogForm] = useState({
    name: "",
    description: "",
    price: 0,
  });
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [savingCatalog, setSavingCatalog] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", description: "", category: "Petrolíferos" });
  const [editingProductId, setEditingProductId] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  // Guides state
  const [guides, setGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [guideForm, setGuideForm] = useState({ title: "", description: "", category: "General" });
  const [guideFile, setGuideFile] = useState(null);
  const [savingGuide, setSavingGuide] = useState(false);

  // Admin modal
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [savingAdmin, setSavingAdmin] = useState(false);

  // Permissions modal
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedAgentForPermissions, setSelectedAgentForPermissions] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/site-settings`, axCfg)
      .then(({ data }) => setSettings(data))
      .catch(() => toast.error("Error al cargar configuracion"));
      
    axios.get(`${API}/admin/me`, axCfg)
      .then(({ data }) => setUserRole(data?.role || "admin"))
      .catch(() => {});
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/agents`, axCfg);
      setAgents(res.data);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error("Error al cargar agentes");
      }
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchServiceCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/service-catalog`, axCfg);
      setServiceCatalog(res.data);
    } catch (e) {
      toast.error("Error al cargar catalogo");
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, axCfg);
      setProducts(res.data);
    } catch (e) {
      console.error("Error loading products");
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const fetchGuides = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/guides`, axCfg);
      setGuides(res.data);
    } catch (e) {
      console.error("Error loading guides");
    } finally {
      setLoadingGuides(false);
    }
  }, []);

  useEffect(() => {
    if (userRole === "admin") {
      fetchAgents();
      fetchProducts();
      fetchGuides();
    }
    fetchServiceCatalog();
  }, [userRole, fetchAgents, fetchServiceCatalog, fetchProducts, fetchGuides]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, axCfg);
      toast.success("Configuracion guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Agent functions
  const openAgentModal = () => {
    setAgentForm({ email: "", password: "", password_confirm: "", name: "" });
    setAgentModalOpen(true);
  };

  const createAgent = async () => {
    if (!agentForm.email.endsWith("@petrocontrol.mx")) {
      toast.error("El correo debe ser @petrocontrol.mx");
      return;
    }
    if (agentForm.password.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    if (agentForm.password !== agentForm.password_confirm) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    if (!agentForm.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setCreatingAgent(true);
    try {
      await axios.post(`${API}/admin/agents`, agentForm, axCfg);
      toast.success("Agente creado exitosamente");
      setAgentModalOpen(false);
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear agente");
    } finally {
      setCreatingAgent(false);
    }
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/agents/${agentId}`,
        { is_active: !currentStatus },
        axCfg
      );
      toast.success(currentStatus ? "Agente desactivado" : "Agente activado");
      fetchAgents();
    } catch (e) {
      toast.error("Error al actualizar estado");
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm("¿Eliminar este agente? Los clientes asignados quedaran sin agente.")) return;
    try {
      await axios.delete(`${API}/admin/agents/${agentId}`, axCfg);
      toast.success("Agente eliminado");
      fetchAgents();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const openChangePasswordModal = (agentId) => {
    setSelectedAgentId(agentId);
    setPasswordForm({ new_password: "", new_password_confirm: "" });
    setChangePasswordModalOpen(true);
  };

  const changeAgentPassword = async () => {
    if (passwordForm.new_password.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirm) {
      toast.error("Las contrasenas no coinciden");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/agents/${selectedAgentId}/change-password`,
        passwordForm,
        axCfg
      );
      toast.success("Contrasena actualizada");
      setChangePasswordModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar contrasena");
    }
  };

  // Service Catalog functions
  const openCatalogModal = (item = null) => {
    if (item) {
      setCatalogForm({ name: item.name, description: item.description, price: item.price });
      setEditingCatalogId(item.id);
    } else {
      setCatalogForm({ name: "", description: "", price: 0 });
      setEditingCatalogId(null);
    }
    setCatalogModalOpen(true);
  };

  const saveCatalogItem = async () => {
    if (!catalogForm.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (catalogForm.price <= 0) {
      toast.error("El precio debe ser mayor a 0");
      return;
    }

    setSavingCatalog(true);
    try {
      if (editingCatalogId) {
        await axios.patch(
          `${API}/admin/service-catalog/${editingCatalogId}`,
          catalogForm,
          axCfg
        );
        toast.success("Servicio actualizado");
      } else {
        await axios.post(`${API}/admin/service-catalog`, catalogForm, axCfg);
        toast.success("Servicio creado");
      }
      setCatalogModalOpen(false);
      fetchServiceCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar servicio");
    } finally {
      setSavingCatalog(false);
    }
  };

  const deleteCatalogItem = async (itemId) => {
    if (!window.confirm("¿Eliminar este servicio del catalogo?")) return;
    try {
      await axios.delete(`${API}/admin/service-catalog/${itemId}`, axCfg);
      toast.success("Servicio eliminado");
      fetchServiceCatalog();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // Product functions
  const openProductModal = (item = null) => {
    if (item) {
      setProductForm({ name: item.name, description: item.description, category: item.category });
      setEditingProductId(item.id);
    } else {
      setProductForm({ name: "", description: "", category: "Petrolíferos" });
      setEditingProductId(null);
    }
    setProductModalOpen(true);
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSavingProduct(true);
    try {
      if (editingProductId) {
        await axios.patch(`${API}/admin/products/${editingProductId}`, productForm, axCfg);
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/admin/products`, productForm, axCfg);
        toast.success("Producto creado");
      }
      setProductModalOpen(false);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar producto");
    } finally {
      setSavingProduct(false);
    }
  };

  const toggleProductStatus = async (productId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}`, { is_active: !currentStatus }, axCfg);
      toast.success(currentStatus ? "Producto desactivado" : "Producto activado");
      fetchProducts();
    } catch (e) {
      toast.error("Error al actualizar estado");
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, axCfg);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // Guide functions
  const saveGuide = async () => {
    if (!guideForm.title.trim() || !guideFile) {
      toast.error("Título y archivo son requeridos");
      return;
    }
    setSavingGuide(true);
    const formData = new FormData();
    formData.append("title", guideForm.title);
    formData.append("description", guideForm.description);
    formData.append("category", guideForm.category);
    formData.append("file", guideFile);

    try {
      await axios.post(`${API}/admin/guides`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Guía subida exitosamente");
      setGuideModalOpen(false);
      setGuideForm({ title: "", description: "", category: "General" });
      setGuideFile(null);
      fetchGuides();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir guía");
    } finally {
      setSavingGuide(false);
    }
  };

  const downloadGuide = async (guideId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/guides/${guideId}/download`, {
        ...axCfg,
        responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const deleteGuide = async (guideId) => {
    if (!window.confirm("¿Eliminar esta guía?")) return;
    try {
      await axios.delete(`${API}/admin/guides/${guideId}`, axCfg);
      toast.success("Guía eliminada");
      fetchGuides();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // Create Admin function
  const createAdmin = async () => {
    if (!adminForm.email || !adminForm.password || !adminForm.name) {
      toast.error("Todos los campos son requeridos");
      return;
    }
    setSavingAdmin(true);
    try {
      await axios.post(`${API}/admin/create-admin`, adminForm, axCfg);
      toast.success("Administrador creado exitosamente");
      setAdminModalOpen(false);
      setAdminForm({ name: "", email: "", password: "" });
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear administrador");
    } finally {
      setSavingAdmin(false);
    }
  };

  // Permissions functions
  const openPermissionsModal = (agent) => {
    setSelectedAgentForPermissions(agent);
    setPermissionsModalOpen(true);
  };

  const updateAgentPermission = async (permKey, value) => {
    if (!selectedAgentForPermissions) return;
    const newPermissions = {
      ...(selectedAgentForPermissions.permissions || {}),
      [permKey]: value
    };
    try {
      await axios.patch(
        `${API}/admin/agents/${selectedAgentForPermissions.id}/permissions`,
        newPermissions,
        axCfg
      );
      setSelectedAgentForPermissions({
        ...selectedAgentForPermissions,
        permissions: newPermissions
      });
      toast.success("Permiso actualizado");
      fetchAgents();
    } catch (e) {
      toast.error("Error al actualizar permiso");
    }
  };

  const updateAgentCommission = async (agentId, rate) => {
    try {
      await axios.patch(`${API}/admin/agents/${agentId}/commission`, { commission_rate: rate }, axCfg);
      toast.success("Comisión actualizada");
      fetchAgents();
    } catch (e) {
      toast.error("Error al actualizar comisión");
    }
  };

  if (!settings) return <p className="text-center py-12 text-gray-500">Cargando configuracion...</p>;

  const siteFields = [
    { key: "firm_name", label: "Nombre de la Empresa" },
    { key: "phone", label: "Telefono" },
    { key: "email", label: "Email de Contacto" },
    { key: "address_line1", label: "Direccion Linea 1" },
    { key: "address_line2", label: "Direccion Linea 2" },
    { key: "whatsapp", label: "WhatsApp (con codigo de pais)" },
  ];

  const bankFields = [
    { key: "banco_nombre", label: "Nombre del Banco" },
    { key: "banco_cuenta", label: "Número de Cuenta" },
    { key: "banco_clabe", label: "CLABE Interbancaria" },
    { key: "banco_beneficiario", label: "Beneficiario" },
    { key: "banco_rfc", label: "RFC del Beneficiario" },
  ];

  return (
    <Tabs defaultValue="site" className="space-y-4">
      <TabsList className="bg-white border flex-wrap">
        <TabsTrigger value="site" className="data-[state=active]:bg-[#1a5c4a] data-[state=active]:text-white">
          <Settings className="h-4 w-4 mr-2" />
          Sitio
        </TabsTrigger>
        <TabsTrigger value="catalog" className="data-[state=active]:bg-[#1a5c4a] data-[state=active]:text-white">
          <DollarSign className="h-4 w-4 mr-2" />
          Catalogo
        </TabsTrigger>
        {userRole === "admin" && (
          <>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#1a5c4a] data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="guides" className="data-[state=active]:bg-[#1a5c4a] data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Guías
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#1a5c4a] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Usuarios
            </TabsTrigger>
          </>
        )}
      </TabsList>

      {/* Site Settings Tab */}
      <TabsContent value="site">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuracion del Sitio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {siteFields.map(f => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input
                    className="mt-1"
                    value={settings[f.key] || ""}
                    onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                    data-testid={`config-${f.key}`}
                  />
                </div>
              ))}
            </div>
            
            {/* Datos Bancarios */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-[#1a5c4a]" />
                Datos Bancarios para Pagos
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Estos datos se usarán en los recordatorios de pago por WhatsApp y en los documentos generados.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {bankFields.map(f => (
                  <div key={f.key}>
                    <Label>{f.label}</Label>
                    <Input
                      className="mt-1"
                      value={settings[f.key] || ""}
                      onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                      data-testid={`config-${f.key}`}
                      placeholder={f.key === "banco_clabe" ? "18 dígitos" : ""}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="mt-6 bg-[#1a5c4a] hover:bg-[#143f34]"
              data-testid="save-config-btn"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Service Catalog Tab */}
      <TabsContent value="catalog">
        <Card className="border-none shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Catalogo de Servicios
            </CardTitle>
            {userRole === "admin" && (
              <Button
                onClick={() => openCatalogModal()}
                className="bg-[#1a5c4a] hover:bg-[#143f34]"
              >
                <Plus className="h-4 w-4 mr-1" />
                Nuevo Servicio
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {userRole === "agent" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Como agente, debes usar los precios del catalogo al crear cotizaciones.
                </p>
              </div>
            )}
            
            {loadingCatalog ? (
              <p className="text-center py-8 text-gray-500">Cargando catalogo...</p>
            ) : serviceCatalog.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No hay servicios en el catalogo</p>
            ) : (
              <div className="space-y-3">
                {serviceCatalog.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 p-4 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <h4 className="font-medium">{item.name}</h4>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                      )}
                      <p className="text-lg font-bold text-[#1a5c4a] mt-1">
                        ${item.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    {userRole === "admin" && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCatalogModal(item)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteCatalogItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Products Tab - Only for main admin */}
      {userRole === "admin" && (
        <TabsContent value="products">
          <Card className="border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Productos / Tipos de Permisos
              </CardTitle>
              <Button
                onClick={() => openProductModal()}
                className="bg-[#1a5c4a] hover:bg-[#143f34]"
                data-testid="new-product-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Nuevo Producto
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Define los tipos de permisos/productos que ofrece Petrocontrol (Autoconsumo, Almacenamiento, etc.)
                </p>
              </div>

              {loadingProducts ? (
                <p className="text-center py-8 text-gray-500">Cargando productos...</p>
              ) : products.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No hay productos registrados</p>
              ) : (
                <div className="space-y-3">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="bg-gray-50 p-4 rounded-lg flex items-center justify-between"
                      data-testid={`product-item-${product.id}`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{product.name}</h4>
                          <Badge className={product.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                            {product.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                          <Badge variant="outline">{product.category}</Badge>
                        </div>
                        {product.description && (
                          <p className="text-sm text-gray-500 mt-1">{product.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openProductModal(product)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Switch
                          checked={product.is_active}
                          onCheckedChange={() => toggleProductStatus(product.id, product.is_active)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteProduct(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      )}

      {/* Guides Tab - Only for main admin */}
      {userRole === "admin" && (
        <TabsContent value="guides">
          <Card className="border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Guías y Regulaciones CNE
              </CardTitle>
              <Button
                onClick={() => setGuideModalOpen(true)}
                className="bg-[#1a5c4a] hover:bg-[#143f34]"
                data-testid="new-guide-btn"
              >
                <Upload className="h-4 w-4 mr-1" />
                Subir Guía
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Sube documentos, manuales y regulaciones de la CNE. Los agentes con permisos podrán consultarlas.
                </p>
              </div>

              {loadingGuides ? (
                <p className="text-center py-8 text-gray-500">Cargando guías...</p>
              ) : guides.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No hay guías subidas</p>
              ) : (
                <div className="space-y-3">
                  {guides.map((guide) => (
                    <div
                      key={guide.id}
                      className="bg-gray-50 p-4 rounded-lg flex items-center justify-between"
                      data-testid={`guide-item-${guide.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-[#1a5c4a]/10 p-2 rounded">
                          <FileText className="h-6 w-6 text-[#1a5c4a]" />
                        </div>
                        <div>
                          <h4 className="font-medium">{guide.title}</h4>
                          {guide.description && (
                            <p className="text-sm text-gray-500">{guide.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{guide.category}</Badge>
                            <span className="text-xs text-gray-400">
                              Subido: {new Date(guide.created_at).toLocaleDateString('es-MX')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadGuide(guide.id, guide.original_filename)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteGuide(guide.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      )}

      {/* Users (Agents) Tab - Only for main admin */}
      {userRole === "admin" && (
        <TabsContent value="users">
          <Card className="border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Usuarios del Sistema
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={() => setAdminModalOpen(true)}
                  variant="outline"
                  className="border-[#1a5c4a] text-[#1a5c4a]"
                  data-testid="new-admin-btn"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Nuevo Admin
                </Button>
                <Button
                  onClick={openAgentModal}
                  className="bg-[#1a5c4a] hover:bg-[#143f34]"
                  data-testid="new-agent-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Nuevo Agente
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Roles del Sistema:</p>
                  <ul className="list-disc ml-4 mt-1">
                    <li><strong>Admin:</strong> Control total, puede crear otros admins y gestionar permisos</li>
                    <li><strong>Agente:</strong> Solo ve clientes/prospectos asignados, debe usar precios del catálogo</li>
                  </ul>
                </div>
              </div>

              {loadingAgents ? (
                <p className="text-center py-8 text-gray-500">Cargando usuarios...</p>
              ) : agents.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No hay usuarios registrados</p>
              ) : (
                <div className="space-y-3">
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className="bg-gray-50 p-4 rounded-lg"
                      data-testid={`user-item-${agent.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{agent.name}</h4>
                            <Badge className={agent.role === "admin" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}>
                              {agent.role === "admin" ? "Admin" : "Agente"}
                            </Badge>
                            <Badge className={agent.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                              {agent.is_active ? "Activo" : "Inactivo"}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">{agent.email}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span>Creado: {new Date(agent.created_at).toLocaleDateString('es-MX')}</span>
                            {agent.role === "agent" && (
                              <span className="flex items-center gap-1">
                                <Percent className="h-3 w-3" />
                                Comisión: {agent.commission_rate || 0}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {agent.role === "agent" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openPermissionsModal(agent)}
                                title="Gestionar Permisos"
                              >
                                <Shield className="h-4 w-4 mr-1" />
                                Permisos
                              </Button>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  className="w-16 h-8 text-xs"
                                  value={agent.commission_rate || 0}
                                  onChange={(e) => updateAgentCommission(agent.id, parseFloat(e.target.value) || 0)}
                                  title="% Comisión"
                                />
                                <span className="text-xs text-gray-500">%</span>
                              </div>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openChangePasswordModal(agent.id)}
                            title="Cambiar contraseña"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Switch
                            checked={agent.is_active}
                            onCheckedChange={() => toggleAgentStatus(agent.id, agent.is_active)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deleteAgent(agent.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      )}

      {/* Agent Modal */}
      <Dialog open={agentModalOpen} onOpenChange={setAgentModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Nuevo Agente
            </DialogTitle>
            <DialogDescription>
              El correo debe ser @petrocontrol.mx
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                className="mt-1"
                placeholder="Nombre del agente"
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Correo Electronico</Label>
              <Input
                className="mt-1"
                type="email"
                placeholder="usuario@petrocontrol.mx"
                value={agentForm.email}
                onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Contrasena</Label>
              <div className="relative">
                <Input
                  className="mt-1 pr-10"
                  type={showPassword ? "text" : "password"}
                  placeholder="Minimo 6 caracteres"
                  value={agentForm.password}
                  onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar Contrasena</Label>
              <Input
                className="mt-1"
                type={showPassword ? "text" : "password"}
                placeholder="Repetir contrasena"
                value={agentForm.password_confirm}
                onChange={(e) => setAgentForm({ ...agentForm, password_confirm: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAgentModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={createAgent}
              disabled={creatingAgent}
            >
              {creatingAgent ? "Creando..." : "Crear Agente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={changePasswordModalOpen} onOpenChange={setChangePasswordModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Cambiar Contrasena
            </DialogTitle>
            <DialogDescription>
              Ingrese la nueva contrasena para el agente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nueva Contrasena</Label>
              <div className="relative">
                <Input
                  className="mt-1 pr-10"
                  type={showPassword ? "text" : "password"}
                  placeholder="Minimo 6 caracteres"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar Nueva Contrasena</Label>
              <Input
                className="mt-1"
                type={showPassword ? "text" : "password"}
                placeholder="Repetir contrasena"
                value={passwordForm.new_password_confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password_confirm: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={changeAgentPassword}
            >
              Cambiar Contrasena
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Catalog Modal */}
      <Dialog open={catalogModalOpen} onOpenChange={setCatalogModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {editingCatalogId ? "Editar Servicio" : "Nuevo Servicio"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre del Servicio</Label>
              <Input
                className="mt-1"
                placeholder="Ej: Permiso CNE Autoconsumo"
                value={catalogForm.name}
                onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Descripcion (opcional)</Label>
              <Textarea
                className="mt-1"
                placeholder="Descripcion del servicio..."
                rows={2}
                value={catalogForm.description}
                onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Precio (MXN)</Label>
              <Input
                className="mt-1"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={catalogForm.price}
                onChange={(e) => setCatalogForm({ ...catalogForm, price: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={saveCatalogItem}
              disabled={savingCatalog}
            >
              {savingCatalog ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={productModalOpen} onOpenChange={setProductModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {editingProductId ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
            <DialogDescription>
              Define los tipos de permisos que maneja Petrocontrol
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre del Producto/Permiso</Label>
              <Input
                className="mt-1"
                placeholder="Ej: Permiso de Autoconsumo"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Textarea
                className="mt-1"
                placeholder="Descripción del tipo de permiso..."
                rows={2}
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                value={productForm.category}
                onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
              >
                <option value="Petrolíferos">Petrolíferos</option>
                <option value="Gas LP">Gas LP</option>
                <option value="Gas Natural">Gas Natural</option>
                <option value="Autoconsumo">Autoconsumo</option>
                <option value="Almacenamiento">Almacenamiento</option>
                <option value="Transporte">Transporte</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={saveProduct}
              disabled={savingProduct}
              data-testid="save-product-btn"
            >
              {savingProduct ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide Upload Modal */}
      <Dialog open={guideModalOpen} onOpenChange={setGuideModalOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Subir Nueva Guía
            </DialogTitle>
            <DialogDescription>
              Sube documentos, manuales y regulaciones de la CNE
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Título de la Guía</Label>
              <Input
                className="mt-1"
                placeholder="Ej: Manual de Requisitos CNE 2024"
                value={guideForm.title}
                onChange={(e) => setGuideForm({ ...guideForm, title: e.target.value })}
                data-testid="guide-title-input"
              />
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Textarea
                className="mt-1"
                placeholder="Descripción del documento..."
                rows={2}
                value={guideForm.description}
                onChange={(e) => setGuideForm({ ...guideForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                value={guideForm.category}
                onChange={(e) => setGuideForm({ ...guideForm, category: e.target.value })}
              >
                <option value="General">General</option>
                <option value="Petrolíferos">Petrolíferos</option>
                <option value="Gas LP">Gas LP</option>
                <option value="Gas Natural">Gas Natural</option>
                <option value="Regulaciones CNE">Regulaciones CNE</option>
                <option value="Manuales">Manuales</option>
                <option value="Formatos">Formatos</option>
              </select>
            </div>
            <div>
              <Label>Archivo (PDF, DOC, XLS)</Label>
              <Input
                className="mt-1"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setGuideFile(e.target.files?.[0] || null)}
                data-testid="guide-file-input"
              />
              {guideFile && (
                <p className="text-xs text-gray-500 mt-1">
                  Seleccionado: {guideFile.name}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setGuideModalOpen(false);
              setGuideFile(null);
              setGuideForm({ title: "", description: "", category: "General" });
            }}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={saveGuide}
              disabled={savingGuide || !guideFile}
              data-testid="save-guide-btn"
            >
              {savingGuide ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Subiendo...
                </>
              ) : "Subir Guía"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Admin Modal */}
      <Dialog open={adminModalOpen} onOpenChange={setAdminModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Crear Administrador
            </DialogTitle>
            <DialogDescription>
              Los administradores tienen control total del sistema
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                className="mt-1"
                placeholder="Nombre del administrador"
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                data-testid="admin-name-input"
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input
                className="mt-1"
                type="email"
                placeholder="admin@empresa.com"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <div className="relative">
                <Input
                  className="mt-1 pr-10"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
              onClick={createAdmin}
              disabled={savingAdmin}
              data-testid="save-admin-btn"
            >
              {savingAdmin ? "Creando..." : "Crear Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Modal */}
      <Dialog open={permissionsModalOpen} onOpenChange={setPermissionsModalOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permisos de {selectedAgentForPermissions?.name}
            </DialogTitle>
            <DialogDescription>
              Configura los permisos específicos de este agente
            </DialogDescription>
          </DialogHeader>

          {selectedAgentForPermissions && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Ver Guías CNE</p>
                  <p className="text-xs text-gray-500">Acceso a documentos y regulaciones</p>
                </div>
                <Switch
                  checked={selectedAgentForPermissions.permissions?.can_view_guides || false}
                  onCheckedChange={(v) => updateAgentPermission("can_view_guides", v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Ver Comisiones</p>
                  <p className="text-xs text-gray-500">Ver su historial de comisiones</p>
                </div>
                <Switch
                  checked={selectedAgentForPermissions.permissions?.can_view_commissions || false}
                  onCheckedChange={(v) => updateAgentPermission("can_view_commissions", v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Crear Cotizaciones</p>
                  <p className="text-xs text-gray-500">Crear cotizaciones para prospectos</p>
                </div>
                <Switch
                  checked={selectedAgentForPermissions.permissions?.can_create_quotations !== false}
                  onCheckedChange={(v) => updateAgentPermission("can_create_quotations", v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Subir Documentos</p>
                  <p className="text-xs text-gray-500">Subir documentos de clientes</p>
                </div>
                <Switch
                  checked={selectedAgentForPermissions.permissions?.can_upload_documents !== false}
                  onCheckedChange={(v) => updateAgentPermission("can_upload_documents", v)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsModalOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
